import * as yup from 'yup';
import { TFunction } from 'i18next';
import useValidators from 'components/Form/validations/useValidators';

const useValidationSchema = (t: TFunction) => {
  const { minNotRequired, min, max } = useValidators();

  const PatientSearchFormSchema = yup.object().shape({
    birthNumber: max(yup.string(), 10),
    firstName: minNotRequired(yup.string(), 2),
    lastName: minNotRequired(yup.string(), 2),
    birthDateFrom: yup.date().typeError(t('Errors:invalidDate')).nullable(),
    birthDateTo: yup.date().typeError(t('Errors:invalidDate')).nullable(),
    city: yup.string(),
    street: yup.string(),
    postalCode: yup.string(),
    state: yup.string(),
    gender: yup.string(),
    deceased: yup.boolean(),
    deceasedDateFrom: yup.date().typeError(t('Errors:invalidDate')).nullable(),
    deceasedDateTo: yup.date().typeError(t('Errors:invalidDate')).nullable(),
    phone: yup.string(),
    email: yup.string().email(t('Errors:invalidEmail')),
  });

  const PatientFormSchema = yup.object().shape({
    active: yup.boolean(),
    firstName: yup.string().required(t('Errors:required')),
    lastName: yup.string().required(t('Errors:required')),
    birthNumber: max(min(yup.string().required(t('Errors:required')), 1), 10),
    birthDate: yup.date().typeError(t('Errors:invalidDate')).nullable(),
    gender: yup.string(),
    deceased: yup.boolean(),
    deceasedDate: yup.date().typeError(t('Errors:invalidDate')).nullable(),
    identifier: yup.array().of(
      yup.object().shape({
        system: yup.string(),
        uiSystemType: yup.string().required(t('Errors:required')),
        oid: yup.string().when('uiSystemType', {
          is: 'other',
          then: (schema) => schema.required(t('Errors:required')),
          otherwise: (schema) => schema.nullable(),
        }),
        value: yup.string().required(t('Errors:required')),
        use: yup.string(),
      }),
    ),
    telecom: yup.array().of(
      yup.object().shape({
        system: yup.string().required(t('Errors:required')),
        value: yup.string().required(t('Errors:required')),
        use: yup.string(),
      }),
    ),
    address: yup.array().of(
      yup.object().shape({
        use: yup.string(),
        type: yup.string(),
        line: yup.array().of(yup.string()),
        city: yup.string(),
        state: yup.string(),
        postalCode: yup.string(),
        country: yup.string(),
      }),
    ),
  });

  const genderItems = [
    { id: '', label: t('sexesAll') },
    { id: 'MALE', label: t('male') },
    { id: 'FEMALE', label: t('female') },
    { id: 'OTHER', label: t('other') },
  ];

  const formItems = [
    {
      type: 'date',
      name: 'birthDateFrom',
      label: t('birthDateFrom'),
    },
    {
      type: 'date',
      name: 'birthDateTo',
      label: t('birthDateTo'),
    },
    {
      type: 'input',
      name: 'postalCode',
      label: t('postalCode'),
    },
    {
      type: 'input',
      name: 'state',
      label: t('state'),
    },
    {
      type: 'input',
      name: 'street',
      label: t('street'),
    },
    {
      type: 'input',
      name: 'phone',
      label: t('phone'),
    },
    {
      type: 'input',
      name: 'email',
      label: t('email'),
    },
    {
      type: 'switch',
      name: 'deceased',
      label: t('deceased'),
    },
  ];

  const extendedFields = [
    'birthDateFrom',
    'birthDateTo',
    'postalCode',
    'state',
    'street',
    'phone',
    'email',
    'deceased',
    'deceasedDateFrom',
    'deceasedDateTo',
  ];

  return {
    PatientSearchFormSchema,
    PatientFormSchema,
    genderItems,
    formItems,
    extendedFields,
  };
};

export default useValidationSchema;
